import { useState, useCallback } from "react";
import { useApi } from "../../../../../../api/ApiProvider";
import { BillPdfBatchItemDtoPdfTypeEnum } from "../../../../../../api-client";
import {
  usePrintGkvBill,
  useExportGkvBills,
  useUpdateGkvBill,
} from "../../../../../../stores/Transactions/insurance-mutation";
import {
  GkvBillEditDto,
  GkvBillPrintResponseDto,
  GkvBillExportResponseDto,
  TestTypeData,
} from "../types/interface";
import useStore from "../../../../../../helpers/useStore";
import { TransactionsContext } from "../../../../../../stores/Transactions/Transactions.provider";
import notificationStore from "../../../../../../stores/Notification/notificationStore";
import { useBatchPrintPdf, BillPdfBatchResult } from "../../../../../../hooks/useBatchPrintPdf";
import {
  extractDocIdsFromBatchResults,
  mergeAndAutoDownload,
} from "../../../../../../utils/billing/documentDownload";

export interface UseGkvEditActionsReturn {
  isPrinting: boolean;
  isExporting: boolean;
  isSaving: boolean;
  /** Print progress (0-100) when using batch print */
  printProgress: number;
  /** Print all bills sequentially. Returns print responses for local patching. */
  printAll: (bills: GkvBillEditDto[]) => Promise<GkvBillPrintResponseDto[]>;
  /** Print all bills in parallel using batch workflow. Returns batch result. */
  printAllBatch: (bills: GkvBillEditDto[], pdfDate?: string) => Promise<BillPdfBatchResult | null>;
  /** Export bills via V2 endpoint. */
  exportAll: (billIds: string[], testTypeData: TestTypeData) => Promise<GkvBillExportResponseDto>;
  /** Save all bills sequentially (persist notes + rxIds). */
  saveAll: (bills: GkvBillEditDto[], notes: Record<string, string>) => Promise<void>;
}

/**
 * Orchestrates the three main edit actions: Print, Export, Save.
 *
 * - Print: sequential per bill (Puppeteer limitation), collects PDFs, merges and auto-downloads.
 * - Export: single V2 endpoint call with all billIds.
 * - Save: sequential per bill (sends notes + rxIds).
 *
 * All use TanStack mutations with per-bill cache invalidation.
 * MobX bridge: updates `transactionStore.lastUpdate` after export so old table refetches.
 */
export function useGkvEditActions(): UseGkvEditActionsReturn {
  const { transaction } = useApi();
  const printMutation = usePrintGkvBill();
  const exportMutation = useExportGkvBills();
  const updateMutation = useUpdateGkvBill();
  const transactionStore = useStore(TransactionsContext);

  const [isPrinting, setIsPrinting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Batch print hook - uses FlowProducer for parallel PDF generation
  const {
    isLoading: isBatchPrinting,
    progress: printProgress,
    startBatchPrint,
  } = useBatchPrintPdf(BillPdfBatchItemDtoPdfTypeEnum.GkvBill);

  /**
   * Print all bills in parallel using the batch workflow.
   * Uses FlowProducer to distribute work across worker pods.
   * Progress is tracked via WebSocket.
   */
  const printAllBatch = useCallback(
    async (bills: GkvBillEditDto[], pdfDate?: string): Promise<BillPdfBatchResult | null> => {
      const billIds = bills.map((b) => b.id);
      const result = await startBatchPrint(billIds, pdfDate);

      if (result) {
        const successfulResults = result.results.filter((r) => r.success);
        const docIds = extractDocIdsFromBatchResults(successfulResults);
        await mergeAndAutoDownload(docIds, (req) => transaction.mergeDirectBillingDocuments(req));

        if (result.failureCount > 0) {
          notificationStore.showMessage(
            `${result.successCount}/${result.totalCount} PDFs erfolgreich erstellt. ${result.failureCount} Fehler.`,
            "warning",
          );
        } else {
          notificationStore.showMessage(
            `Alle ${result.totalCount} PDFs erfolgreich erstellt.`,
            "success",
          );
        }
        // Signal MobX table to refetch
        transactionStore.lastUpdate = new Date();
      }

      return result;
    },
    [startBatchPrint, transaction, transactionStore],
  );

  /**
   * Print all bills sequentially. After all prints, merge PDFs and trigger download.
   */
  const printAll = useCallback(
    async (bills: GkvBillEditDto[]): Promise<GkvBillPrintResponseDto[]> => {
      setIsPrinting(true);
      const results: GkvBillPrintResponseDto[] = [];

      try {
        for (const bill of bills) {
          const response = await printMutation.mutateAsync({ billId: bill.id });
          results.push(response);
        }

        // Extract doc IDs via the shared batch extractor for consistent mapping.
        const syntheticBatchResults = results.map((response, index) => ({
          success: true,
          entityId: bills[index].id,
          pdfType: BillPdfBatchItemDtoPdfTypeEnum.GkvBill,
          durationMs: 0,
          paperDocumentId: response.paperCenterReceipt?.id,
          billDocumentId: response.billPdf?.id,
        }));
        const docIds = extractDocIdsFromBatchResults(syntheticBatchResults as any);
        await mergeAndAutoDownload(docIds, (req) => transaction.mergeDirectBillingDocuments(req));

        notificationStore.showMessage(
          "Urbelegbegleitschreiben wurde erfolgreich als PDF erstellt.",
          "success",
        );

        return results;
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.message || error?.message || "Unbekannter Fehler";
        notificationStore.showMessage(
          `Beim Drucken der Rechnung ist ein Problem aufgetreten: ${errorMessage}`,
          "error",
        );
        throw error;
      } finally {
        setIsPrinting(false);
      }
    },
    [printMutation, transaction],
  );

  /**
   * Export bills via the V2 batch endpoint.
   */
  const exportAll = useCallback(
    async (billIds: string[], testTypeData: TestTypeData): Promise<GkvBillExportResponseDto> => {
      setIsExporting(true);
      try {
        const response = await exportMutation.mutateAsync({
          billIds,
          testTypeData: { type: testTypeData.type, email: testTypeData.email },
        });

        // Bridge: signal old MobX table to refetch
        transactionStore.lastUpdate = new Date();

        return response;
      } finally {
        setIsExporting(false);
      }
    },
    [exportMutation, transactionStore],
  );

  /**
   * Save all bills sequentially (notes + rxIds + amount).
   */
  const saveAll = useCallback(
    async (bills: GkvBillEditDto[], notes: Record<string, string>): Promise<void> => {
      setIsSaving(true);
      try {
        for (const bill of bills) {
          await updateMutation.mutateAsync({
            billId: bill.id,
            dto: {
              rxIds: bill.rxs.map((rx) => rx.id),
              amount: bill.amount,
              notes: notes[bill.id] || "",
            },
          });
        }
        notificationStore.showMessage("Alle Transaktionen erfolgreich aktualisiert", "success");
      } catch (error) {
        notificationStore.showMessage("Fehler beim Aktualisieren der Transaktionen", "error");
        throw error;
      } finally {
        setIsSaving(false);
      }
    },
    [updateMutation],
  );

  return {
    isPrinting: isPrinting || isBatchPrinting,
    isExporting,
    isSaving,
    printProgress,
    printAll,
    printAllBatch,
    exportAll,
    saveAll,
  };
}
